/**
 * Pinia-store для единой схемы полей Product.
 *
 * Источник истины: backend `/products/schema` (генерируется из
 * `backend/schemas/product_fields.py` — реестр SSOT).
 *
 * Используется компонентами через composable `useProductSchema()` —
 * см. `frontend/src/composables/useProductSchema.js`.
 *
 * Лениво загружает схему при первом обращении, кэширует на 5 минут.
 * После CRUD кастомной колонки в AdminColumnsPage вызвать `invalidate()`,
 * чтобы остальные открытые в той же сессии компоненты подтянули обновлённую схему.
 */
import { defineStore } from 'pinia'
import customColumnsService from '@/services/customColumnsService'

const TTL_MS = 5 * 60 * 1000

export const useProductSchemaStore = defineStore('productSchema', {
  state: () => ({
    nativeFields: [],
    customFields: [],
    loaded: false,
    loadedAt: 0,
    loadingPromise: null,
  }),

  getters: {
    allFields: (s) => [...s.nativeFields, ...s.customFields],

    fieldsByGroup: (s) => {
      const groups = {}
      for (const f of [...s.nativeFields, ...s.customFields]) {
        const g = f.group_name || 'Прочее'
        ;(groups[g] ||= []).push(f)
      }
      return groups
    },

    fieldByKey: (s) => (key) =>
      [...s.nativeFields, ...s.customFields].find((f) => f.key === key),

    filterableFields: (s) =>
      [...s.nativeFields, ...s.customFields].filter((f) => f.is_filterable),

    sortableFields: (s) =>
      [...s.nativeFields, ...s.customFields].filter((f) => f.is_sortable),

    /**
     * Поля, доступные для выбора в экспорте Excel.
     * Исключаются только контейнеры (custom_fields), кастомные поля включаются.
     */
    exportableFields: (s) =>
      [...s.nativeFields, ...s.customFields].filter((f) => f.key !== 'custom_fields'),
  },

  actions: {
    /**
     * Гарантирует, что схема загружена. Идемпотентно, безопасно вызывать многократно.
     * @param {boolean} force — принудительно перезагрузить, игнорируя TTL.
     * @returns {Promise<void>}
     */
    async ensureLoaded(force = false) {
      if (this.loaded && !force && Date.now() - this.loadedAt < TTL_MS) return
      if (this.loadingPromise) return this.loadingPromise

      this.loadingPromise = (async () => {
        try {
          const schema = await customColumnsService.getProductSchema()
          this.nativeFields = schema.native_fields || []
          this.customFields = schema.custom_fields || []
          this.loaded = true
          this.loadedAt = Date.now()
        } catch (err) {
          console.error('[productSchemaStore] Не удалось загрузить /products/schema:', err)
          throw err
        } finally {
          this.loadingPromise = null
        }
      })()

      return this.loadingPromise
    },

    /**
     * Сбрасывает кэш. Дёрнуть после CRUD кастомной колонки.
     */
    invalidate() {
      this.loaded = false
      this.loadedAt = 0
      this.loadingPromise = null
    },
  },
})
